import { createElement, useRef } from 'react';

import { ALPHA2_TO_ALPHA3 } from 'modules/constants';
import { findTranslationForLocale } from 'modules/forms/utils';
import { useI18n } from 'modules/i18n';
import { useProject } from 'modules/routes';
import { Spinner } from 'modules/spinners';
import { InlineNotification } from 'modules/ui';
import PropTypes from 'prop-types';

import { InputField, RichTextarea, Textarea } from '../input';

/**
 * Renders translated inputs for the loaded project locales without changing stored translation IDs.
 * @param {Object} props - Field configuration, persisted translations, and change/validation callbacks.
 * @returns {React.ReactElement} Localized fields or project loading/error feedback.
 */
export default function MultiLocaleWrapper(props) {
    const { t } = useI18n();
    const { project, isLoading, error } = useProject();
    const locales = project?.available_locales || [];

    const {
        attribute,
        elementType,
        scope,
        label,
        mandatory,
        data, // Persisted data
        formValues, // Current form values which may include unsaved changes
        handleChange,
        handleErrors,
        validate,
        origAsLocale,
        baseLocales,
    } = props;

    const localEditedValuesRef = useRef({});
    if (error)
        return (
            <InlineNotification
                variant="error"
                title={t('error')}
                description={error.message}
                isClosable={false}
            />
        );
    if (isLoading || !project) return <Spinner />;

    let usedLocales = origAsLocale ? [...locales, 'orig'] : locales;
    if (baseLocales?.length) {
        usedLocales = Array.from(new Set([...usedLocales, ...baseLocales]));
    }

    const usedLocale = (locale) => {
        const alpha3 = ALPHA2_TO_ALPHA3[locale];
        return data?.type === 'Segment' ? alpha3 : locale;
    };

    const labelFunc = (locale) => {
        return (
            (label || t(`activerecord.attributes.${scope}.${attribute}`)) +
            (mandatory ? ' *' : '') +
            ` (${usedLocale(locale)})`
        );
    };

    const findTranslation = (locale) => {
        return findTranslationForLocale(data, formValues, locale);
    };

    const getLiveTranslationValue = (localeKey) => {
        // Prefer just-typed values because parent form state updates asynchronously.
        if (
            Object.prototype.hasOwnProperty.call(
                localEditedValuesRef.current,
                localeKey
            )
        ) {
            return localEditedValuesRef.current[localeKey];
        }

        return findTranslation(localeKey)?.[attribute];
    };

    const updateMultiLocaleErrorState = () => {
        if (
            typeof validate !== 'function' ||
            typeof handleErrors !== 'function'
        ) {
            return;
        }

        // Multi-locale fields are valid when at least one locale passes validation.
        const hasAnyValidTranslation = usedLocales.some((locale) =>
            validate(getLiveTranslationValue(usedLocale(locale)))
        );

        handleErrors(attribute, !hasAnyValidTranslation);
    };

    const onChange = (name, value, translation) => {
        localEditedValuesRef.current[translation.locale] = value;

        let params = {
            translation: {
                [name]: value,
                locale: translation.locale,
                id: translation.id,
            },
        };
        handleChange(null, null, params, 'locale');

        updateMultiLocaleErrorState();
    };

    const preparedProps = (locale) => {
        const translation = findTranslation(usedLocale(locale)) || {
            locale: usedLocale(locale),
        };
        const value = translation[attribute];

        return Object.assign({}, props, {
            handleChange: onChange,
            data: translation,
            value: value,
            label: labelFunc(locale),
            key: `${attribute}-${locale}`,
            id: `${attribute}_${locale}`,
            locale: usedLocale(locale),
        });
    };

    const components = {
        input: InputField,
        richTextEditor: RichTextarea,
        textarea: Textarea,
    };

    return (
        <div className="multi-locale-input">
            {usedLocales.map((locale) => {
                return createElement(
                    components[elementType],
                    preparedProps(locale)
                );
            })}
        </div>
    );
}

MultiLocaleWrapper.propTypes = {
    attribute: PropTypes.string,
    elementType: PropTypes.string,
    scope: PropTypes.string,
    label: PropTypes.string,
    mandatory: PropTypes.bool,
    baseLocales: PropTypes.arrayOf(PropTypes.string),
    data: PropTypes.object,
    formValues: PropTypes.object,
    handleChange: PropTypes.func,
    handleErrors: PropTypes.func,
    validate: PropTypes.func,
    origAsLocale: PropTypes.bool,
    value: PropTypes.any,
    accept: PropTypes.string,
    condition: PropTypes.bool,
};
